"use client";

/**
 * Marketing-only static slice of the customer portal estimate card (`app/portal/portal-client.tsx`).
 * Kept compact for the homepage — not the full portal page.
 */
import { ArrowRight, Building2, Check, FileText, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export function MarketingCustomerPortalSample() {
  return (
    <div
      className={cn(
        // Match real portal proportions (~max-w-3xl card feel), not a narrow phone column — avoids squished two-column layout
        "w-full max-w-[min(38rem,100%)] overflow-hidden rounded-xl border border-border bg-background shadow-[0_20px_40px_-24px_rgba(15,23,42,0.45)]",
        "text-sm leading-snug text-foreground sm:max-w-[min(40rem,100%)] lg:max-w-[min(42rem,100%)]"
      )}
    >
      <Card className="border-0 shadow-none">
        <CardContent className="p-5 sm:p-6">
          {/* md+: side-by-side like live /portal; below that stack so the mock never pinches the copy column */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-800 ring-1 ring-sky-200">
                  You&apos;ve opened this
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                  Review to approve, sign, or pay
                </span>
              </div>
              <div className="flex items-start gap-2">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <h4 className="text-lg font-semibold leading-snug text-foreground">
                  Kitchen backsplash &amp; cabinet refresh
                </h4>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span>Harborline Home Improvements</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Updated yesterday</span>
                <span className="hidden sm:inline">·</span>
                <span>May 8, 2026, 6:39 PM</span>
              </div>
              <p className="text-xs text-muted-foreground">Pricing shown through May 20, 2026</p>

              <div className="space-y-0.5 pt-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total</p>
                <p className="text-2xl font-bold leading-tight text-foreground">$8,942</p>
              </div>

              <div className="pt-2">
                <p className="text-sm font-semibold text-foreground">Remaining balance</p>
                <p className="mt-0.5 text-sm text-foreground">$6,706 left on this estimate.</p>
                <p className="mt-1 text-xs text-muted-foreground">Deposit terms: 25% deposit required.</p>
              </div>

              <div className="space-y-3 border-t border-border/70 pt-3 text-xs text-muted-foreground">
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Timeline</p>
                  <p>Sent: May 2, 2026, 1:12 AM</p>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Company</p>
                  <p>Harborline Home Improvements</p>
                  <p>
                    <a
                      href="mailto:estimates@harborlinehome.com"
                      className="text-primary underline-offset-2 hover:underline"
                      tabIndex={-1}
                    >
                      estimates@harborlinehome.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="space-y-1 border-t border-border/70 pt-3">
                <div className="flex min-h-[1.25rem] items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground">Your private note</Label>
                  <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-700 opacity-0">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    Saved on this device
                  </span>
                </div>
                <Textarea
                  readOnly
                  tabIndex={-1}
                  className="min-h-[62px] resize-none text-sm"
                  placeholder="Write things to ask about later..."
                  value=""
                  aria-hidden
                />
                <p className="text-xs text-muted-foreground">
                  Notes save automatically on this device only—they are not sent to the business.
                </p>
              </div>
            </div>

            <div className="flex w-full shrink-0 flex-col gap-2 self-stretch md:w-[9.25rem] md:self-center">
              <Button type="button" className="h-10 gap-2 font-semibold shadow-sm" tabIndex={-1} aria-hidden>
                View estimate
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
              <Button type="button" size="sm" variant="secondary" className="h-9 justify-start" tabIndex={-1}>
                <MessageSquare className="mr-1.5 h-4 w-4" />
                Message
              </Button>
              <Button type="button" size="sm" variant="secondary" className="h-9 justify-start" tabIndex={-1}>
                Pay now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
